import { z } from 'zod';
import { audioAnalysisProviderSchema } from './audio-transcription';
import { webSearchProviderSchema } from './web-tools';

export const RUNTIME_SETTINGS_DOMAINS = ['web', 'chat', 'llm', 'prompts'] as const;
export const RUNTIME_SETTINGS_SCHEMA_VERSION = 1 as const;
export const RUNTIME_SETTINGS_PROMPT_PREVIEW_SCENARIOS = [
  'conversation_web_on',
  'conversation_web_off',
  'conversation_attachments',
  'rag',
  'web_standard',
  'deep',
] as const;

// `searxngEngines` was removed with SearXNG (2026-10-06): stored values are ignored on read.
const WEB_RUNTIME_SETTING_KEYS = ['searchProvider'] as const;
const CHAT_RUNTIME_SETTING_KEYS = ['newConversationWebSearchDefaultEnabled'] as const;
const LLM_RUNTIME_SETTING_KEYS = ['chatProvider', 'utilityProvider'] as const;
const PROMPTS_RUNTIME_SETTING_KEYS = [
  'conversation',
  'rag',
  'webStandard',
  'deep',
] as const;

export const runtimeSettingsDomainSchema = z.enum(RUNTIME_SETTINGS_DOMAINS, {
  error: 'Le domaine de paramètres runtime est invalide.',
});

// Staan is the only web search provider (decision 2026-10-06). A persisted legacy
// `searxng` value is normalized server-side on read, never offered as a choice.
export const webRuntimeSettingsSchema = z.object({
  searchProvider: webSearchProviderSchema.optional(),
}).strict();

export const chatRuntimeSettingsSchema = z.object({
  newConversationWebSearchDefaultEnabled: z.boolean({
    error: 'Le défaut de recherche web des nouvelles conversations est invalide.',
  }).optional(),
}).strict();

export const chatRuntimeDefaultsSchema = z.object({
  audioAnalysisProvider: audioAnalysisProviderSchema.optional(),
  audioTranscriptionEnabled: z.boolean({
    error: 'Le flag de transcription audio est invalide.',
  }),
  // Composer dictation (ASR-only instance). Optional: absent from older backends.
  dictationEnabled: z.boolean().optional(),
  newConversationWebSearchDefaultEnabled: z.boolean({
    error: 'Le défaut effectif de recherche web des nouvelles conversations est invalide.',
  }),
}).strict();

// LLM calls are self-hosted only (ADR-2026-10-06): no other provider is
// selectable. Persisted legacy values are normalized server-side on read.
export const LLM_PROVIDERS = ['selfhosted'] as const;
export const llmProviderSchema = z.enum(LLM_PROVIDERS, {
  error: 'Le fournisseur LLM du chat est invalide.',
});
export const llmUtilityProviderSchema = z.enum([...LLM_PROVIDERS, 'same_as_chat'], {
  error: 'Le fournisseur LLM utilitaire est invalide.',
});

export const llmRuntimeSettingsSchema = z.object({
  chatProvider: llmProviderSchema.optional(),
  utilityProvider: llmUtilityProviderSchema.optional(),
}).strict();

export const PROMPT_PERSONA_FIELD_LIMITS = {
  additionalPersonaNotes: 800,
  assistantIdentity: 200,
  // Champ fusionné « Ton & style de réponse » (absorbe l'ancien languageStyle).
  toneGuidelines: 1000,
} as const;

export const promptPersonaSchema = z.object({
  additionalPersonaNotes: z.string()
    .trim()
    .min(1, { error: 'Les instructions métier ne peuvent pas être vides.' })
    .max(PROMPT_PERSONA_FIELD_LIMITS.additionalPersonaNotes, {
      error: 'Les instructions métier ne peuvent pas dépasser 800 caractères.',
    })
    .optional(),
  assistantIdentity: z.string()
    .trim()
    .min(1, { error: 'L’identité de l’assistant ne peut pas être vide.' })
    .max(PROMPT_PERSONA_FIELD_LIMITS.assistantIdentity, {
      error: 'L’identité de l’assistant ne peut pas dépasser 200 caractères.',
    })
    .optional(),
  toneGuidelines: z.string()
    .trim()
    .min(1, { error: 'Le ton & style de réponse ne peut pas être vide.' })
    .max(PROMPT_PERSONA_FIELD_LIMITS.toneGuidelines, {
      error: 'Le ton & style de réponse ne peut pas dépasser 1000 caractères.',
    })
    .optional(),
}).strict();

const promptPersonaFormFieldSchema = {
  additionalPersonaNotes: z.string().max(PROMPT_PERSONA_FIELD_LIMITS.additionalPersonaNotes, {
    error: 'Les instructions métier ne peuvent pas dépasser 800 caractères.',
  }).optional(),
  assistantIdentity: z.string().max(PROMPT_PERSONA_FIELD_LIMITS.assistantIdentity, {
    error: 'L’identité de l’assistant ne peut pas dépasser 200 caractères.',
  }).optional(),
  toneGuidelines: z.string().max(PROMPT_PERSONA_FIELD_LIMITS.toneGuidelines, {
    error: 'Le ton & style de réponse ne peut pas dépasser 1000 caractères.',
  }).optional(),
} as const;

/** Schéma de formulaire : chaînes vides autorisées, bornes conservées. */
export const promptPersonaFormSchema = z.object(promptPersonaFormFieldSchema).strict();

export const promptsRuntimeSettingsSchema = z.object({
  conversation: promptPersonaSchema.optional(),
  deep: promptPersonaSchema.optional(),
  rag: promptPersonaSchema.optional(),
  webStandard: promptPersonaSchema.optional(),
}).strict();

export const promptsPersonaFormSchema = z.object({
  conversation: promptPersonaFormSchema.optional(),
  deep: promptPersonaFormSchema.optional(),
  rag: promptPersonaFormSchema.optional(),
  webStandard: promptPersonaFormSchema.optional(),
}).strict();

export const globalPromptsPreviewRequestSchema = z.object({
  set: promptsRuntimeSettingsSchema,
}).strict();

const PERSONA_COMPACT_FIELDS = [
  'assistantIdentity',
  'toneGuidelines',
  'additionalPersonaNotes',
] as const;

export function compactPromptPersona(
  input: Partial<Record<(typeof PERSONA_COMPACT_FIELDS)[number], string | undefined>>,
): z.infer<typeof promptPersonaSchema> {
  const compacted: Partial<Record<(typeof PERSONA_COMPACT_FIELDS)[number], string>> = {};
  for (const field of PERSONA_COMPACT_FIELDS) {
    const trimmed = input[field]?.trim();
    if (trimmed) compacted[field] = trimmed;
  }
  return promptPersonaSchema.parse(compacted);
}

export function compactPromptsRuntimeSettings(
  input: z.infer<typeof promptsPersonaFormSchema>,
): z.infer<typeof promptsRuntimeSettingsSchema> {
  const set: z.infer<typeof promptsRuntimeSettingsSchema> = {};
  for (const mode of PROMPTS_RUNTIME_SETTING_KEYS) {
    const persona = input[mode];
    if (persona === undefined) continue;
    set[mode] = compactPromptPersona(persona);
  }
  return promptsRuntimeSettingsSchema.parse(set);
}

function createRuntimeSettingsWriteSchema<
  SettingsSchema extends z.ZodObject,
  SettingKeySchema extends z.ZodType<string>,
>(settingsSchema: SettingsSchema, settingKeySchema: SettingKeySchema) {
  return z.object({
    expectedVersion: z.number({
      error: 'La version attendue est invalide.',
    }).int({ error: 'La version attendue doit être un entier.' }).nonnegative({
      error: 'La version attendue ne peut pas être négative.',
    }),
    note: z.string()
      .trim()
      .max(1_000, { error: 'La note ne peut pas dépasser 1000 caractères.' })
      .optional(),
    set: settingsSchema,
    unset: z.array(settingKeySchema, {
      error: 'La liste des paramètres à réinitialiser est invalide.',
    }).transform((keys) => [...new Set(keys)]),
  }).strict().superRefine((value, ctx) => {
    if (
      !('set' in value)
      || !('unset' in value)
      || typeof value.set !== 'object'
      || value.set === null
      || !Array.isArray(value.unset)
    ) {
      return;
    }

    const setValues = value.set as object;
    const overlappingKeys: string[] = [];
    for (const key of value.unset as readonly unknown[]) {
      if (typeof key === 'string' && Object.hasOwn(setValues, key)) {
        overlappingKeys.push(key);
      }
    }
    if (overlappingKeys.length === 0) return;

    ctx.addIssue({
      code: 'custom',
      message: `Un paramètre ne peut pas être présent dans set et unset : ${overlappingKeys.join(', ')}.`,
      path: ['unset'],
    });
  });
}

export const webRuntimeSettingsWriteSchema = createRuntimeSettingsWriteSchema(
  webRuntimeSettingsSchema,
  z.enum(WEB_RUNTIME_SETTING_KEYS),
);
export const chatRuntimeSettingsWriteSchema = createRuntimeSettingsWriteSchema(
  chatRuntimeSettingsSchema,
  z.enum(CHAT_RUNTIME_SETTING_KEYS),
);
export const llmRuntimeSettingsWriteSchema = createRuntimeSettingsWriteSchema(
  llmRuntimeSettingsSchema,
  z.enum(LLM_RUNTIME_SETTING_KEYS),
);
export const promptsRuntimeSettingsWriteSchema = createRuntimeSettingsWriteSchema(
  promptsRuntimeSettingsSchema,
  z.enum(PROMPTS_RUNTIME_SETTING_KEYS),
);

export const runtimeSettingSourceSchema = z.enum(['default', 'env', 'db'], {
  error: 'La source du paramètre runtime est invalide.',
});

export const runtimeSettingUnselectableReasonSchema = z.enum([
  'capability_unavailable',
  'release_gate_closed',
], {
  error: 'Le code d’indisponibilité du paramètre runtime est invalide.',
});

function createRuntimeSettingFieldSchema<ValueSchema extends z.ZodType>(
  configuredValueSchema: ValueSchema,
) {
  return z.object({
    configuredValue: configuredValueSchema,
    selectable: z.boolean(),
    source: runtimeSettingSourceSchema,
    unselectableReason: runtimeSettingUnselectableReasonSchema.optional(),
  }).strict();
}

const runtimeSettingsReadEnvelopeSchema = z.object({
  schemaVersion: z.number().int().positive(),
  updatedAt: z.iso.datetime({
    error: 'La date de mise à jour des paramètres runtime est invalide.',
  }).optional(),
  updatedBy: z.string().trim().min(1).optional(),
  version: z.number().int().nonnegative(),
}).strict();

export const webRuntimeSettingsReadSchema = runtimeSettingsReadEnvelopeSchema.extend({
  domain: z.literal('web'),
  settings: z.object({
    searchProvider: createRuntimeSettingFieldSchema(webSearchProviderSchema.nullable()),
  }).strict(),
}).strict();

export const chatRuntimeSettingsReadSchema = runtimeSettingsReadEnvelopeSchema.extend({
  domain: z.literal('chat'),
  settings: z.object({
    newConversationWebSearchDefaultEnabled: createRuntimeSettingFieldSchema(z.boolean()),
  }).strict(),
}).strict();

export const llmRuntimeSettingsReadSchema = runtimeSettingsReadEnvelopeSchema.extend({
  domain: z.literal('llm'),
  settings: z.object({
    chatProvider: createRuntimeSettingFieldSchema(llmProviderSchema),
    utilityProvider: createRuntimeSettingFieldSchema(llmUtilityProviderSchema),
  }).strict(),
}).strict();

export const promptsRuntimeSettingsReadSchema = runtimeSettingsReadEnvelopeSchema.extend({
  domain: z.literal('prompts'),
  settings: z.object({
    conversation: createRuntimeSettingFieldSchema(promptPersonaSchema),
    deep: createRuntimeSettingFieldSchema(promptPersonaSchema),
    rag: createRuntimeSettingFieldSchema(promptPersonaSchema),
    webStandard: createRuntimeSettingFieldSchema(promptPersonaSchema),
  }).strict(),
}).strict();

export const runtimeSettingsReadSchema = z.discriminatedUnion('domain', [
  webRuntimeSettingsReadSchema,
  chatRuntimeSettingsReadSchema,
  llmRuntimeSettingsReadSchema,
  promptsRuntimeSettingsReadSchema,
]);

export const runtimeSettingsPromptPreviewScenarioSchema = z.object({
  id: z.enum(RUNTIME_SETTINGS_PROMPT_PREVIEW_SCENARIOS),
  label: z.string().trim().min(1),
  prompt: z.string().min(1),
}).strict();

export const runtimeSettingsPromptPreviewResponseSchema = z.array(
  runtimeSettingsPromptPreviewScenarioSchema,
).length(RUNTIME_SETTINGS_PROMPT_PREVIEW_SCENARIOS.length);

const runtimeSettingsJsonObjectSchema = z.record(z.string(), z.unknown());

export const runtimeSettingsAuditEventSchema = z.object({
  actorLabel: z.string().trim().min(1),
  appRelease: z.string().trim().min(1).optional(),
  changedBy: z.string().trim().min(1).optional(),
  changedKeys: z.array(z.string().trim().min(1)),
  createdAt: z.iso.datetime({
    error: 'La date de l’événement de paramètres runtime est invalide.',
  }),
  domain: runtimeSettingsDomainSchema,
  id: z.string().trim().min(1),
  nextValue: runtimeSettingsJsonObjectSchema,
  note: z.string().trim().max(1_000).optional(),
  previousValue: runtimeSettingsJsonObjectSchema,
  requestId: z.string().trim().min(1).optional(),
  versionAfter: z.number().int().nonnegative(),
  versionBefore: z.number().int().nonnegative(),
}).strict();

const runtimeSettingsPaginationMetaSchema = z.object({
  page: z.number().int().min(1),
  pageCount: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().nonnegative(),
}).strict();

export const paginatedRuntimeSettingsAuditEventsSchema = z.object({
  items: z.array(runtimeSettingsAuditEventSchema),
  meta: runtimeSettingsPaginationMetaSchema,
}).strict();

export const runtimeOptionReadinessSchema = z.object({
  available: z.boolean().nullable(),
  gateOpen: z.boolean(),
  selectable: z.boolean(),
  status: z.enum(['ready', 'unavailable', 'unknown']),
}).strict();

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// A backend-web deployed before 2026-10-06 still reports `allowedEngines` and
// `providers.searxng`: these legacy keys are dropped before the strict parse so that a
// rolling deployment keeps readiness readable. Any other unknown key is still refused.
function dropLegacyWebCapabilities(value: unknown): unknown {
  if (!isPlainRecord(value)) return value;
  const current: Record<string, unknown> = { ...value };
  delete current.allowedEngines;
  if (isPlainRecord(current.providers)) {
    const providers: Record<string, unknown> = { ...current.providers };
    delete providers.searxng;
    current.providers = providers;
  }
  return current;
}

export const webToolsCapabilitiesSchema = z.preprocess(dropLegacyWebCapabilities, z.object({
  providers: z.object({
    staan: z.object({
      authLatchedInvalid: z.boolean(),
      budgetPositive: z.boolean(),
      configured: z.boolean(),
      ledgerAvailable: z.boolean(),
    }).strict(),
  }).strict(),
}).strict());

export const runtimeSettingsReadinessSchema = z.object({
  llm: z.object({
    selfhosted: runtimeOptionReadinessSchema,
  }).strict(),
  web: z.object({
    capabilities: webToolsCapabilitiesSchema.nullable(),
    staan: runtimeOptionReadinessSchema,
  }).strict(),
}).strict();

export type RuntimeSettingsDomain = z.infer<typeof runtimeSettingsDomainSchema>;
export type RuntimeSettingSource = z.infer<typeof runtimeSettingSourceSchema>;
export type WebRuntimeSettingsDTO = z.infer<typeof webRuntimeSettingsSchema>;
export type ChatRuntimeSettingsDTO = z.infer<typeof chatRuntimeSettingsSchema>;
export type ChatRuntimeDefaultsDTO = z.infer<typeof chatRuntimeDefaultsSchema>;
export type LlmProviderDTO = z.infer<typeof llmProviderSchema>;
export type LlmUtilityProviderDTO = z.infer<typeof llmUtilityProviderSchema>;
export type LlmRuntimeSettingsDTO = z.infer<typeof llmRuntimeSettingsSchema>;
export type PromptPersonaDTO = z.infer<typeof promptPersonaSchema>;
export type PromptPersonaFormDTO = z.infer<typeof promptPersonaFormSchema>;
export type PromptsRuntimeSettingsDTO = z.infer<typeof promptsRuntimeSettingsSchema>;
export type PromptsPersonaFormDTO = z.infer<typeof promptsPersonaFormSchema>;
export type GlobalPromptsPreviewRequestDTO = z.infer<typeof globalPromptsPreviewRequestSchema>;
export type WebRuntimeSettingsWriteDTO = z.infer<typeof webRuntimeSettingsWriteSchema>;
export type ChatRuntimeSettingsWriteDTO = z.infer<typeof chatRuntimeSettingsWriteSchema>;
export type LlmRuntimeSettingsWriteDTO = z.infer<typeof llmRuntimeSettingsWriteSchema>;
export type PromptsRuntimeSettingsWriteDTO = z.infer<typeof promptsRuntimeSettingsWriteSchema>;
export type WebRuntimeSettingsReadDTO = z.infer<typeof webRuntimeSettingsReadSchema>;
export type ChatRuntimeSettingsReadDTO = z.infer<typeof chatRuntimeSettingsReadSchema>;
export type LlmRuntimeSettingsReadDTO = z.infer<typeof llmRuntimeSettingsReadSchema>;
export type PromptsRuntimeSettingsReadDTO = z.infer<typeof promptsRuntimeSettingsReadSchema>;
export type RuntimeSettingsReadDTO = z.infer<typeof runtimeSettingsReadSchema>;
export type RuntimeSettingsPromptPreviewScenarioDTO = z.infer<
  typeof runtimeSettingsPromptPreviewScenarioSchema
>;
export type RuntimeSettingsPromptPreviewResponseDTO = z.infer<
  typeof runtimeSettingsPromptPreviewResponseSchema
>;
export type RuntimeSettingsAuditEventDTO = z.infer<typeof runtimeSettingsAuditEventSchema>;
export type PaginatedRuntimeSettingsAuditEventsDTO = z.infer<
  typeof paginatedRuntimeSettingsAuditEventsSchema
>;
export type WebToolsCapabilitiesDTO = z.infer<typeof webToolsCapabilitiesSchema>;
export type RuntimeOptionReadinessDTO = z.infer<typeof runtimeOptionReadinessSchema>;
export type RuntimeSettingsReadinessDTO = z.infer<typeof runtimeSettingsReadinessSchema>;
